/**
 * Shared React Query surface for admin mailboxes (SA-92). Mirrors
 * hooks/usePronunciationDictionaries.ts: one list query + one mutation
 * per verb, each invalidating the shared list key on success.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  mailboxesService,
  type BackloadRange,
  type BackloadStart,
  type Mailbox,
  type MailboxCreate,
  type MailboxUpdate,
  type WahaSourceRead,
} from '@/services/mailboxes'

export const MAILBOXES_KEY = ['mailboxes'] as const

/** SA-759: while any mailbox loads earlier mail, the list refetches so its
 *  row shows progress (a run loads a chunk per poll tick). */
export function backloadRefetchInterval(mailboxes: Mailbox[] | undefined): number | false {
  return mailboxes?.some((mb) => mb.backload?.status === 'loading') ? 10_000 : false
}

export function useMailboxes() {
  return useQuery({
    queryKey: MAILBOXES_KEY,
    queryFn: mailboxesService.list,
    refetchInterval: (query) => backloadRefetchInterval(query.state.data),
  })
}

export function useCreateMailbox() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: MailboxCreate) => mailboxesService.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function useUpdateMailbox() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & MailboxUpdate) =>
      mailboxesService.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function useDeleteMailbox() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, keepSession }: { id: string; keepSession?: boolean }) =>
      keepSession ? mailboxesService.remove(id, { keepSession: true }) : mailboxesService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function useConnectMailbox() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, account }: { id: string; account?: string }) =>
      account ? mailboxesService.connect(id, account) : mailboxesService.connect(id),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
      qc.invalidateQueries({ queryKey: wahaKey(id) })
    },
  })
}

export function useConfirmMailboxAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, account }: { id: string; account: string }) => mailboxesService.confirmAccount(id, account),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function usePauseMailbox() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => mailboxesService.pause(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function useResumeMailbox() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => mailboxesService.resume(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function usePollMailboxNow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => mailboxesService.pollNow(id),
    // onSettled, not onSuccess: a 502 (MailboxAuthError) still flips the
    // mailbox to reauth_required server-side (routers/mailboxes.py) before
    // it re-raises — the list must refetch on that error path too, or the
    // status flip is invisible until the next unrelated refetch.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

/** SA-759: count what a backload over a range would load (nothing is cached). */
export function usePreviewBackload() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & BackloadRange) =>
      mailboxesService.previewBackload(id, body),
  })
}

export function useStartBackload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & BackloadStart) =>
      mailboxesService.startBackload(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

export function useCancelBackload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, jobId }: { id: string; jobId: string }) =>
      mailboxesService.cancelBackload(id, jobId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    },
  })
}

/** SA-717: the handlers a routing policy can bind (fetched when an editor opens). */
export function useMailboxHandlers(enabled: boolean) {
  return useQuery({
    queryKey: [...MAILBOXES_KEY, 'handlers'],
    queryFn: mailboxesService.handlers,
    staleTime: 60_000,
    enabled,
  })
}

/** SA-717: who changed this mailbox's binding, newest first (fetched when
 *  its editor opens). Under MAILBOXES_KEY, so a saved policy refetches it. */
export function useMailboxPolicyChanges(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...MAILBOXES_KEY, id, 'policy-changes'],
    queryFn: () => mailboxesService.policyChanges(id as string),
    enabled: enabled && id !== null,
  })
}

/** SA-717: one router mailbox's triage metrics (fetched when its panel opens). */
export function useTriageMetrics(id: string, days: 30 | 90) {
  return useQuery({
    queryKey: [...MAILBOXES_KEY, id, 'triage-metrics', days],
    queryFn: () => mailboxesService.triageMetrics(id, days),
    staleTime: 30_000,
  })
}

/** SA-947 (spec P8): the providers this deployment offers. Its own key, so a
 *  mailbox write does not refetch it. */
export const MAILBOX_PROVIDERS_KEY = ['mailbox-providers'] as const

export function useMailboxProviders() {
  return useQuery({
    queryKey: MAILBOX_PROVIDERS_KEY,
    queryFn: mailboxesService.providers,
    staleTime: 5 * 60_000,
  })
}

/** SA-947 (spec P12): a group source's connect reads, under one key. */
export function wahaKey(id: string) {
  return ['mailbox-waha', id] as const
}

export function useWahaStatus(
  id: string,
  enabled: boolean,
  interval: (read: WahaSourceRead | undefined) => number | false,
) {
  return useQuery({
    queryKey: [...wahaKey(id), 'status'],
    queryFn: () => mailboxesService.connectStatus(id),
    enabled,
    // Each opening reads the session afresh: the app's queries stay fresh for
    // five minutes, and a cached step would freeze the dialog (final review).
    staleTime: 0,
    refetchInterval: (query) => interval(query.state.data),
  })
}

export function useWahaQr(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...wahaKey(id), 'qr'],
    queryFn: () => mailboxesService.qr(id),
    enabled,
    refetchInterval: enabled ? 20_000 : false,
    gcTime: 0,
  })
}

export function useWahaGroups(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...wahaKey(id), 'groups'],
    queryFn: () => mailboxesService.groups(id),
    enabled,
  })
}

export function useRequestPairingCode() {
  return useMutation({
    mutationFn: ({ id, phoneNumber }: { id: string; phoneNumber: string }) =>
      mailboxesService.requestPairingCode(id, phoneNumber),
  })
}

export function useBindGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, groupId }: { id: string; groupId: string }) => mailboxesService.bind(id, groupId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
      qc.invalidateQueries({ queryKey: wahaKey(id) })
    },
  })
}

export function useUnbindGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => mailboxesService.unbind(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
      qc.invalidateQueries({ queryKey: wahaKey(id) })
    },
  })
}
